import { Link } from "react-router-dom";
import {
  ArrowRight, Heart, MessageSquareText, Calculator, FlaskConical, PersonStanding, Palette,
  Blocks, Brush, Cpu, Drama, TreePine, Hand, Baby, BookOpen, Rocket, GraduationCap, School, Shirt,
} from "lucide-react";
import { IMAGES } from "@/constants/site";
import { Reveal, Stagger, StaggerItem, Squiggle } from "@/components/site/Reveal";
import { EditorialMarquee } from "@/components/site/Marquee";

const PROGRAMS = [
  {
    icon: Baby, name: "Toddler Class", age: "18 months – 3 yrs", color: "bg-brand-sky", chip: "text-brand-deepsky bg-brand-sky/15",
    focus: "Gentle separation from home, sensory exploration and first friendships in a warm, unhurried space.",
    activities: ["Sensory bins & water play", "Rhymes, music & movement", "Free play & sandbox time", "Fine-motor toddler stations"],
  },
  {
    icon: Blocks, name: "Nursery", age: "3 – 4 yrs", color: "bg-brand-coral", chip: "text-brand-deepcoral bg-brand-coral/15",
    focus: "Language blooms — phonics sounds, storytelling and confident daily routines.",
    activities: ["Storytelling & puppet time", "Pre-writing strokes & patterns", "Number rhymes & counting games", "Role play & show-and-tell"],
  },
  {
    icon: Rocket, name: "Pre-Kindergarten", age: "4+ yrs", color: "bg-brand-sun", chip: "text-amber-700 bg-brand-sun/20",
    focus: "School readiness — early reading, writing and number sense built through play.",
    activities: ["Phonics & early reading", "Hands-on math with manipulatives", "Art projects & craft", "Group games & turn-taking"],
  },
  {
    icon: BookOpen, name: "Pre-Primary", age: "4+ yrs", color: "bg-emerald-500", chip: "text-emerald-700 bg-emerald-100",
    focus: "Strong foundations on the RBSE pattern — Hindi & English literacy with joyful discipline.",
    activities: ["Hindi & English literacy", "Mental math & tables play", "EVS discovery walks", "Value education & good habits"],
  },
  {
    icon: GraduationCap, name: "Classes 1st – 8th", age: "6+ yrs", color: "bg-brand-navy", chip: "text-brand-deepsky bg-sky-100",
    focus: "Concept clarity, discipline and all-round growth through the RBSE curriculum.",
    activities: ["Smart-class concept lessons", "Weekly assessments & feedback", "Sports, yoga & PT", "Olympiad & scholarship prep"],
  },
];

const PILLARS = [
  { icon: Heart, name: "Social & Emotional", desc: "Sharing, empathy and self-confidence" },
  { icon: MessageSquareText, name: "Language & Literacy", desc: "Phonics, vocabulary and expression" },
  { icon: Calculator, name: "Early Math", desc: "Numbers, patterns and problem-solving" },
  { icon: FlaskConical, name: "Science & Discovery", desc: "Experiments, nature and big questions" },
  { icon: PersonStanding, name: "Motor Skills", desc: "Balance, coordination and control" },
  { icon: Palette, name: "Creativity", desc: "Art, music, drama and imagination" },
];

const FACILITIES = [
  { icon: Blocks, name: "Learning Centers" },
  { icon: Brush, name: "Art & Creativity Station" },
  { icon: Cpu, name: "STEM Zone" },
  { icon: Drama, name: "Dramatic Play" },
  { icon: TreePine, name: "Nature Garden" },
  { icon: Hand, name: "Sensory Play" },
  { icon: School, name: "Physical Play Structures" },
];

export default function Programs() {
  return (
    <main data-testid="programs-page" className="pb-20 md:pb-0 pt-36 md:pt-44">
      <header className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-4">Programs</p>
          <h1 className="font-display text-5xl md:text-7xl font-semibold tracking-tight leading-[1.02] max-w-3xl">
            From first steps to
            <span className="relative inline-block text-brand-deepcoral"> big leaps
              <Squiggle className="absolute -bottom-2 left-0 w-full text-brand-sun" />
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-slate-600">
            Five age-wise programs on the RBSE pattern — each one playful, purposeful and personal.
          </p>
        </Reveal>
      </header>

      {/* PROGRAMS */}
      <section className="mx-auto max-w-7xl px-6 py-24 space-y-24">
        {PROGRAMS.map((p, i) => (
          <div key={p.name} className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 ? "" : ""}`}>
            <Reveal className={i % 2 ? "lg:order-2" : ""}>
              <div className={`relative overflow-hidden ${i % 2 ? "blob-b" : "blob-a"} border-8 border-white shadow-[0_25px_60px_rgba(2,132,199,0.15)]`}>
                <img
                  src={[IMAGES.toddler, IMAGES.art, IMAGES.preschool, IMAGES.circle, IMAGES.outing][i]}
                  alt={p.name}
                  className="aspect-[4/3] w-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal delay={0.12} className={i % 2 ? "lg:order-1" : ""}>
              <div className="flex items-center gap-4">
                <span className={`grid h-14 w-14 place-items-center rounded-2xl ${p.color} text-white shadow-lg`}>
                  <p.icon className="h-7 w-7" />
                </span>
                <div>
                  <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">{p.name}</h2>
                  <span className={`mt-1 inline-block rounded-full px-3.5 py-1 text-xs font-bold ${p.chip}`}>{p.age}</span>
                </div>
              </div>
              <p className="mt-5 text-slate-600 leading-relaxed">{p.focus}</p>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {p.activities.map((a) => (
                  <li key={a} className="flex items-center gap-3 rounded-2xl bg-white border border-amber-200/50 px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${p.color}`} /> {a}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        ))}
      </section>

      <EditorialMarquee />

      {/* CURRICULUM PILLARS */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <Reveal className="mb-14 max-w-2xl">
          <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-deepsky mb-3">Curriculum Pillars</p>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight">Six pillars of early learning</h2>
        </Reveal>
        <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.map((c) => (
            <StaggerItem key={c.name}>
              <div data-testid={`pillar-${c.name.toLowerCase().replace(/[^a-z]/g, "-")}`} className="group h-full rounded-3xl border border-amber-200/50 bg-white p-7 shadow-[0_15px_40px_rgba(2,132,199,0.07)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(251,113,133,0.15)]">
                <span className="grid h-13 w-13 place-items-center rounded-2xl bg-brand-coral/15 p-3 text-brand-deepcoral transition-transform duration-300 group-hover:rotate-6">
                  <c.icon className="h-7 w-7" />
                </span>
                <h3 className="mt-4 font-display text-xl font-semibold">{c.name}</h3>
                <p className="mt-1.5 text-sm text-slate-600">{c.desc}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* FACILITIES */}
      <section className="bg-brand-navy py-24 md:py-32 grain relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-brand-sun/15 blur-3xl" />
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-brand-sun mb-3">Our Campus</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-white">Spaces built for little explorers</h2>
          </Reveal>
          <Stagger className="grid gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {FACILITIES.map((f, i) => (
              <StaggerItem key={f.name} className={i === 0 ? "col-span-2 lg:col-span-2" : ""}>
                <div data-testid={`facility-${f.name.toLowerCase().replace(/[^a-z]/g, "-")}`} className="group flex h-full items-center gap-4 rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur transition-all duration-300 hover:-translate-y-1.5 hover:bg-white/10">
                  <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-brand-sky/20 text-brand-sky transition-transform duration-300 group-hover:scale-110">
                    <f.icon className="h-6 w-6" />
                  </span>
                  <p className="font-display font-semibold text-white">{f.name}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>

          {/* UNIFORM POLICY */}
          <Reveal className="mt-16">
            <div data-testid="uniform-policy" className="flex flex-col md:flex-row items-start md:items-center gap-6 rounded-[2.5rem] bg-brand-sun p-8 md:p-10 text-ink shadow-xl">
              <span className="grid h-16 w-16 shrink-0 place-items-center rounded-3xl bg-white/70 text-brand-deepcoral">
                <Shirt className="h-8 w-8" />
              </span>
              <div>
                <h3 className="font-display text-2xl font-semibold">Uniform & Belongings</h3>
                <p className="mt-2 text-ink/75 leading-relaxed max-w-3xl">
                  A simple, comfortable uniform keeps mornings easy. Please label all belongings — water bottle, tiffin,
                  bag and an extra set of clothes. Toddlers may attend in comfortable home clothes until they settle in.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal className="mt-14 text-center">
            <Link to="/admissions" data-testid="programs-cta" className="group inline-flex min-h-[52px] items-center gap-2 rounded-full bg-brand-coral px-8 font-bold text-white shadow-[0_10px_30px_rgba(251,113,133,0.4)] transition-transform duration-300 hover:scale-105">
              Enquire About a Program <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
